import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CheckCircle, AlertTriangle } from "lucide-react";
import logger from "@/lib/logger";
import { formatDateTimeIST } from "@/lib/formatUtils";

export default function StaleDraftsTab() {
  const [staleDrafts, setStaleDrafts] = useState([]);

  const fetchStaleDrafts = useCallback(async () => {
    try {
      const { data } = await api.get("/drafts/stale/list");
      setStaleDrafts(data.drafts || []);
    } catch (err) { logger.error("Failed to fetch stale drafts:", err); }
  }, []);

  useEffect(() => { fetchStaleDrafts(); }, [fetchStaleDrafts]);

  return (
    <Card className="mt-4 p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-bold" style={{ fontFamily: "Chivo" }}>Stale Drafts (24h+)</h3>
          <p className="text-xs text-gray-500">Drafts older than 24 hours that may need attention</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchStaleDrafts} className="h-8 text-xs" data-testid="refresh-stale-btn">
          Refresh
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="bg-amber-100/50">
            <TableHead className="text-xs uppercase font-bold">Type</TableHead>
            <TableHead className="text-xs uppercase font-bold">Location</TableHead>
            <TableHead className="text-xs uppercase font-bold">Created By</TableHead>
            <TableHead className="text-xs uppercase font-bold">Created At</TableHead>
            <TableHead className="text-xs uppercase font-bold">Last Updated</TableHead>
            <TableHead className="text-xs uppercase font-bold">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {staleDrafts.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-8 text-gray-400">
                <CheckCircle size={20} className="inline mr-2 text-emerald-500" />No stale drafts
              </TableCell>
            </TableRow>
          )}
          {staleDrafts.map(d => (
            <TableRow key={d.id} className="hover:bg-amber-50/50">
              <TableCell><Badge variant="outline" className="text-xs">{d.draft_type}</Badge></TableCell>
              <TableCell className="text-sm">{d.location || "\u2014"}</TableCell>
              <TableCell className="text-xs">{d.created_by?.split("@")[0]}</TableCell>
              <TableCell className="text-xs">{d.created_at ? formatDateTimeIST(d.created_at) : "\u2014"}</TableCell>
              <TableCell className="text-xs">{d.updated_at ? formatDateTimeIST(d.updated_at) : "\u2014"}</TableCell>
              <TableCell>
                <Badge className="bg-amber-100 text-amber-700 text-[10px]">
                  <AlertTriangle size={10} className="mr-0.5" /> Stale
                </Badge>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
